import { HttpHandlerFn, HttpRequest, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Observable, from, switchMap, catchError, shareReplay } from 'rxjs';
import { LoginService } from '../services/login.service';

// Variables globales pour la gestion du refresh
let refreshInProgress: Observable<any> | null = null;
let lastRefreshFailTime: number = 0;

// Configuration
const REFRESH_RETRY_DELAY = 30000; // 30 secondes avant de retenter (configurable)
const TOKEN_THRESHOLD_MINUTES = 10; // Seuil pour renouveler le token (configurable)

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const loginService: LoginService = inject(LoginService);

  if (req.url.includes('accounts.google.com')) {
    return next(req);
  }

  // Exclure l'endpoint de refresh token pour éviter les conflits
  if (req.url.includes('/auth/refresh')) {
    return next(req);
  }

  const token: string | null = localStorage.getItem('token');
  const refreshToken: string | null = localStorage.getItem('refreshToken');

  if (!token) {
    return next(req);
  }

  const decodedToken: JwtPayload | null = token ? jwtDecode(token) : null;
  const thresholdMinutes = TOKEN_THRESHOLD_MINUTES; // Utilise la constante configurable
  const minutesRemaining =
    decodedToken && decodedToken.exp
      ? (decodedToken.exp * 1000 - Date.now()) / 60000
      : -999;

  const tokenIsExpired = minutesRemaining <= 0;
  const tokenIsNearExpiry =
    minutesRemaining > 0 && minutesRemaining < thresholdMinutes;

  // Si le token est complètement expiré, nettoyer et rediriger
  if (tokenIsExpired) {
    // console.log(
    //   `🔴 TOKEN EXPIRÉ (${Math.floor(
    //     minutesRemaining
    //   )}m) - Nettoyage et redirection...`
    // );
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

    // Réinitialiser les variables globales
    refreshInProgress = null;
    lastRefreshFailTime = 0;

    // Continuer sans token - l'AuthGuard se chargera de la redirection
    return next(req);
  }
  // else {
  //   console.log(
  //     `🟢 Token encore valide pour ${Math.floor(
  //       minutesRemaining
  //     )}m ${Math.floor((minutesRemaining % 1) * 60)}s`
  //   );
  // }

  // Renouveler le token SEULEMENT s'il est proche de l'expiration (mais encore valide)
  if (tokenIsNearExpiry) {
    // Vérifier si un refresh a récemment échoué
    const timeSinceLastFailure = Date.now() - lastRefreshFailTime;
    if (timeSinceLastFailure < REFRESH_RETRY_DELAY) {
      // console.log(
      //   `⏳ Refresh en attente: ${Math.ceil(
      //     (REFRESH_RETRY_DELAY - timeSinceLastFailure) / 1000
      //   )}s restantes`
      // );
      // Continuer avec le token actuel (encore valide pour quelques minutes)
      const authReq = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + token),
      });
      return next(authReq);
    }

    // console.log(
    //   `🟡 TOKEN PROCHE EXPIRATION: ${Math.floor(
    //     minutesRemaining
    //   )}m restantes - Tentative de renouvellement...`
    // );

    if (!refreshToken) {
      console.error('🔴 Aucun refresh token disponible');
      return next(req);
    }

    // Vérifier si un refresh est déjà en cours
    if (refreshInProgress) {
      // console.log('🔄 Renouvellement déjà en cours, attente...');
      return refreshInProgress.pipe(
        switchMap((authResponse) => {
          // Utiliser le nouveau token pour cette requête
          const authReq = req.clone({
            headers: req.headers.set(
              'Authorization',
              'Bearer ' + authResponse.token
            ),
          });
          return next(authReq);
        })
      );
    }

    // Créer et lancer le refresh
    refreshInProgress = from(loginService.refresh(refreshToken)).pipe(
      switchMap((authResponse) => {
        // console.log('✅ TOKEN RENOUVELÉ avec succès');

        // Mettre à jour les tokens dans le localStorage
        loginService.setStorageAccessToken(
          authResponse.token,
          authResponse.ref_token
        );

        // Réinitialiser les variables
        refreshInProgress = null;
        lastRefreshFailTime = 0; // Reset le timer d'échec

        return from([authResponse]);
      }),
      catchError((error) => {
        console.error('🔴 ÉCHEC DU RENOUVELLEMENT DU TOKEN:', error);

        // Réinitialiser les variables et marquer l'heure d'échec
        refreshInProgress = null;
        lastRefreshFailTime = Date.now();

        // En cas d'échec, retourner le token actuel
        return from([{ token: token, ref_token: refreshToken }]);
      }),
      shareReplay(1)
    );

    // Attendre le résultat du refresh et utiliser le token approprié
    return refreshInProgress.pipe(
      switchMap((authResponse) => {
        const tokenToUse = authResponse.token;
        const authReq = req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + tokenToUse),
        });
        return next(authReq);
      })
    );
  }

  // Token valide, continuer avec la requête
  const authReq = req.clone({
    headers: req.headers.set('Authorization', 'Bearer ' + token),
  });
  return next(authReq);
}
